
import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { toast } from '@/hooks/use-toast';
import { findRouteMeta } from '@/app/registry/routes';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { getFirstAccessiblePage } from '@/utils/getFirstAccessiblePage';

// Em desenvolvimento o StrictMode roda o efeito duas vezes: um aviso por tela basta.
let ultimoAviso = { caminho: '', em: 0 };

/**
 * Redireciona quem não tem permissão e avisa o porquê. Antes o usuário caía em outra tela sem explicação
 * (28/10/2026).
 */
const RedirecionarSemPermissao: React.FC<{ to: string }> = ({ to }) => {
  const location = useLocation();

  useEffect(() => {
    const agora = Date.now();
    if (ultimoAviso.caminho === location.pathname && agora - ultimoAviso.em < 2000) return;
    ultimoAviso = { caminho: location.pathname, em: agora };

    const tela = findRouteMeta(location.pathname)?.title;
    toast({
      title: 'Sem permissão',
      description: `Você não tem acesso ${tela ? `à tela "${tela}"` : 'a esta tela'}. Peça a liberação a um administrador.`,
    });
  }, [location.pathname]);

  return <Navigate to={to} replace />;
};

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermission?: string | string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredPermission 
}) => {
  const { isAuthenticated, isLoading, hasPermission, user } = useAuth();
  const location = useLocation();

  // Mostrar loading enquanto verifica autenticação
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  // Redirecionar para login se não estiver autenticado
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Verificar permissão específica se requerida
  if (requiredPermission) {
    const requiredPermissions = Array.isArray(requiredPermission) ? requiredPermission : [requiredPermission];
    const hasAccess = requiredPermissions.some((perm) => hasPermission(perm));

    if (!hasAccess) {
      // Tentar encontrar a primeira página acessível
      const firstAccessiblePage = getFirstAccessiblePage(hasPermission);
      
      if (firstAccessiblePage) {
        return <RedirecionarSemPermissao to={firstAccessiblePage} />;
      }
      
      // Se não encontrou nenhuma página acessível, mostrar mensagem de erro
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center max-w-md mx-auto px-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <h2 className="text-2xl font-bold text-red-800 mb-2">Acesso Negado</h2>
              <p className="text-red-600 mb-4">
                Você não tem permissão para acessar esta página e não possui acesso a nenhuma outra página do sistema.
              </p>
              <p className="text-sm text-gray-600">
                Usuário: <strong>{user?.nome}</strong>
              </p>
              <p className="text-sm text-gray-600">
                Perfil: <strong>{user?.perfil_padrao || 'Não definido'}</strong>
              </p>
              <p className="text-sm text-gray-600">
                Página requerida: <strong>{requiredPermissions.join(', ')}</strong>
              </p>
              <details className="mt-4 text-left">
                <summary className="text-sm text-gray-600 cursor-pointer hover:text-gray-800">
                  Ver permissões do usuário
                </summary>
                <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-auto max-h-40">
                  {JSON.stringify(user?.permissoes, null, 2)}
                </pre>
              </details>
            </div>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
